import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { DATE_V3, SELECT_V2 } from '@app/formly/helpers/fields';
import { toISO8601 } from '@app/formly/types/date/date-helpers';
import { mapUserToDropdown } from '@app/pages/users/forms/user-form/user-form.component';
import {
  AgentSubstitutionService,
  toSubstitutionDateTime,
  toSubstitutionEndDateTime,
} from '@app/shared/services/agent-substitution/agent-substitution.service';
import { AgentSubstitution } from '@app/shared/services/agent-substitution/agent-substitution.service';
import { RadarFormAbstract } from '@app/shared/components/radar-form-abstract';
import { finalize, map } from 'rxjs/operators';

@Component({
  selector: 'agent-substitution-form',
  templateUrl: './agent-substitution-form.component.html',
  styleUrls: ['./agent-substitution-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentSubstitutionFormComponent extends RadarFormAbstract implements OnInit {
  activeSubstitution: AgentSubstitution | null = null;
  loading = true;
  submitting = false;

  ngOnInit(): void {
    super.ngOnInit();
    this.setCreateFields();
    this.loadActiveSubstitution();
  }

  activateSubstitution() {
    if (this.activeSubstitution || !this.formGroup.valid || this.submitting) {
      return;
    }

    const model = this.formModel;

    this.submitting = true;

    this.agentSubstitutionService
      .create({
        coveringAgentId: model.coveringAgentId as string,
        startAt: new Date(toSubstitutionDateTime(model.startAt as string)),
        endAt: model.endAt ? new Date(toSubstitutionEndDateTime(model.endAt as string)) : undefined,
      })
      .pipe(finalize(() => this.setSubmitting(false)))
      .subscribe((substitution) => {
        this.activeSubstitution = substitution;
        this.applyActiveSubstitution(substitution);
      });
  }

  cancelSubstitution() {
    if (!this.activeSubstitution?.id || this.submitting) {
      return;
    }

    this.submitting = true;

    this.agentSubstitutionService
      .end(this.activeSubstitution.id)
      .pipe(finalize(() => this.setSubmitting(false)))
      .subscribe(() => {
        this.activeSubstitution = null;
        this.resetCreateForm();
      });
  }

  loadActiveSubstitution() {
    this.loading = true;
    this.cdRef.markForCheck();

    this.agentSubstitutionService
      .getActive()
      .pipe(finalize(() => this.setLoading(false)))
      .subscribe((substitution) => {
        this.activeSubstitution = substitution;

        if (substitution) {
          this.applyActiveSubstitution(substitution);
        } else {
          this.resetCreateForm();
        }
      });
  }

  applyActiveSubstitution(substitution: AgentSubstitution) {
    const agent = substitution.coveringAgent;

    // `startDate` / `throughDate` are the backend's own reading of the absence, in the absent
    // agent's shift zone as of its start. They are typed `Date` but arrive as raw `YYYY-MM-DD`
    // strings, which is what this form's date fields hold — assign them RAW. Putting either
    // through `new Date()` / `toISO8601` would re-render it in the BROWSER's zone and show a
    // viewer west of that shift zone the day before. `throughDate` is null iff open-ended.
    this.formModel = {
      coveringAgentId: agent?.id,
      startAt: substitution.startDate ?? toISO8601(new Date()),
      endAt: substitution.throughDate ?? null,
    };

    if (this.fields[0]?.props && agent) {
      this.fields[0].props.initial = {
        value: agent.id,
        label: agent.fullName,
      };
    }

    this.formGroup.patchValue(this.formModel as Record<string, unknown>);
    this.syncFormDisabledState(true);
    this.cdRef.markForCheck();
  }

  toFormDate(value: Date | string): string {
    return toISO8601(value instanceof Date ? value : new Date(value));
  }

  syncFormDisabledState(disabled: boolean) {
    if (disabled) {
      this.formGroup.disable();
    } else {
      this.formGroup.enable();
    }

    this.fields = this.fields.map((field) => ({
      ...field,
      props: {
        ...field.props,
        disabled,
      },
    }));
  }

  setLoading(loading: boolean) {
    this.loading = loading;
    this.cdRef.markForCheck();
  }

  setSubmitting(submitting: boolean) {
    this.submitting = submitting;
    this.cdRef.markForCheck();
  }

  setCreateFields() {
    const agents$ = this.agentSubstitutionService
      .getAvailableAgents()
      .pipe(map((agents) => agents.map(mapUserToDropdown)));

    this.fields = [
      SELECT_V2(
        {
          key: 'coveringAgentId',
          props: {
            label: 'Who will cover for you?',
            required: true,
          },
        },
        agents$,
      ),
      DATE_V3({
        key: 'startAt',
        props: {
          label: 'Start date',
          required: true,
        },
        defaultValue: toISO8601(new Date()),
      }),
      DATE_V3({
        key: 'endAt',
        props: {
          label: 'End date',
          placeholder: 'No end date',
          required: false,
        },
      }),
    ];
  }

  resetCreateForm() {
    if (this.fields[0]?.props) {
      this.fields[0].props.initial = null;
    }

    this.formModel = {
      coveringAgentId: null,
      startAt: toISO8601(new Date()),
      endAt: null,
    };
    this.formGroup.reset(this.formModel);
    this.formGroup.markAsPristine();
    this.syncFormDisabledState(false);
    this.cdRef.markForCheck();
  }

  constructor(
    private agentSubstitutionService: AgentSubstitutionService,
    private cdRef: ChangeDetectorRef,
  ) {
    super();
  }
}
